// Static position evaluation - "how good does this game state look for
// playerId," used as the leaf/terminal scorer for heuristicBot.js's
// lookahead rollout search (see that file's own header comment for the
// full design). Not a win/loss prediction, a differential estimate: how
// much better/worse this player's position looks than their strongest
// opponent's, on a scale where a real, certain win/loss always dominates
// any mid-game estimate.

import { getInstanceDefinition } from '../../cards/cardLookup';
import { getPlayerCollectionInstanceIds, getPlayerById } from '../../state/selectors';
import { computeFinalScores, computePlayerScore } from '../../scoring/scoring';
import { cardValue } from './villageHeuristics';

// Large enough that any terminal win/loss outranks every possible
// non-terminal estimate below (VP/deck-quality/XP terms are all small,
// bounded-ish numbers - a few hundred at most for a real game) - search
// should always prefer "the game is over and I won" over "my position
// looks great but the game continues."
const WIN_VALUE = 10000;

// VP is the actual win condition, so it dominates; deck quality (this
// player's overall card pool, via villageHeuristics.js's cardValue) is a
// mid-game predictor of future VP/combat strength, weighted down since raw
// cardValue sums across a 15-30 card collection run much larger than a
// typical mid-game VP total and shouldn't drown it out; XP is a minor
// resource signal. Starting weights, not yet tuned - see
// scripts/rosters/lookahead.json for the sweep that validates or adjusts
// these once search itself is confirmed to help at all.
const VP_WEIGHT = 10;
const DECK_QUALITY_WEIGHT = 0.3;
const XP_WEIGHT = 1;

function estimatePlayerValue(state, playerId) {
  const vp = computePlayerScore(state, playerId) * VP_WEIGHT;
  const deckQuality = getPlayerCollectionInstanceIds(state, playerId)
    .reduce((sum, id) => sum + cardValue(getInstanceDefinition(state, id)), 0) * DECK_QUALITY_WEIGHT;
  const xp = (getPlayerById(state, playerId).xp || 0) * XP_WEIGHT;
  return vp + deckQuality + xp;
}

// Returns a signed number: positive means playerId looks ahead, negative
// means behind, magnitude reflects how much. Differential (this player's
// estimate minus their strongest surviving opponent's), not absolute -
// adversarial 2-player search cares about relative standing, not raw score.
export function evaluatePosition(state, playerId) {
  if (state.meta.gameOver) {
    const { winnerId, scores, tied } = computeFinalScores(state);
    if (tied) return 0;
    const myPoints = scores.find((s) => s.playerId === playerId)?.points || 0;
    const oppPoints = scores.filter((s) => s.playerId !== playerId).map((s) => s.points);
    const bestOppPoints = oppPoints.length ? Math.max(...oppPoints) : 0;
    return winnerId === playerId
      ? WIN_VALUE + (myPoints - bestOppPoints)
      : -WIN_VALUE - (bestOppPoints - myPoints);
  }

  const myValue = estimatePlayerValue(state, playerId);
  const opponents = state.players.filter((p) => p.id !== playerId && !p.eliminated);
  const bestOppValue = opponents.length ? Math.max(...opponents.map((p) => estimatePlayerValue(state, p.id))) : 0;
  return myValue - bestOppValue;
}
